<template>
  <div>
    <b-row class="mb-2">
      <!-- Download by link -->
      <b-col cols="12" lg="6">
        <b-card class="mb-2 mb-lg-0 h-100" border-variant="secondary">
          <h6>Скачать по ссылке</h6>
          <b-input-group>
            <b-form-input
                v-model="downloadUrl"
                placeholder="Ссылка VK или YouTube"
                :disabled="isDownloadingByUrl"
                @keyup.enter="downloadByUrl"
            />
            <b-input-group-append>
              <b-button
                  variant="success"
                  :disabled="!downloadUrl || isDownloadingByUrl"
                  @click="downloadByUrl"
              >
                <b-spinner v-if="isDownloadingByUrl" small class="mr-50"/>
                <feather-icon v-else icon="DownloadIcon" class="mr-50"/>
                Скачать
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-card>
      </b-col>

      <!-- Upload MP3 button -->
      <b-col cols="12" lg="6">
        <b-card class="mb-0 h-100" border-variant="secondary">
          <div class="d-flex align-items-center justify-content-between">
            <h6 class="mb-0">Загрузить MP3 файл</h6>
            <b-button
                variant="primary"
                @click="showUploadModal = true"
            >
              <feather-icon icon="UploadIcon" class="mr-50"/>
              Загрузить
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Upload MP3 modal -->
    <b-modal
        v-model="showUploadModal"
        title="Загрузить MP3 файл"
        centered
        no-close-on-backdrop
        hide-footer
    >
      <div
          class="upload-drop-zone"
          :class="{
            'drop-zone-active': isDragOver,
            'drop-zone-has-file': uploadFile,
            'drop-zone-uploading': isUploadingMp3,
          }"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="onFileDrop"
          @click="$refs.mp3FileInput.click()"
      >
        <input
            ref="mp3FileInput"
            type="file"
            accept=".mp3,audio/mpeg"
            class="d-none"
            @change="onFileSelect"
        />
        <div v-if="isUploadingMp3" class="text-center">
          <b-spinner variant="primary" class="mb-1"/>
          <p class="mb-0">Загрузка...</p>
        </div>
        <div v-else-if="uploadFile" class="text-center">
          <feather-icon icon="MusicIcon" size="32" class="text-success mb-1"/>
          <p class="mb-0 font-weight-bold">{{ uploadFile.name }}</p>
          <small class="text-muted">{{ formatFileSize(uploadFile.size) }}</small>
        </div>
        <div v-else class="text-center">
          <feather-icon icon="UploadCloudIcon" size="48" class="text-muted mb-1"/>
          <p class="mb-50">Перетащите MP3 файл сюда</p>
          <small class="text-muted">или нажмите для выбора файла (макс. 50 МБ)</small>
        </div>
      </div>
      <div v-if="uploadError" class="mt-1">
        <b-alert show variant="danger" class="mb-0 p-75">
          {{ uploadError }}
        </b-alert>
      </div>
      <div class="d-flex justify-content-end mt-2">
        <b-button
            variant="outline-secondary"
            class="mr-1"
            :disabled="isUploadingMp3"
            @click="closeUploadModal"
        >
          Отмена
        </b-button>
        <b-button
            variant="success"
            :disabled="!uploadFile || isUploadingMp3"
            @click="uploadMp3"
        >
          <b-spinner v-if="isUploadingMp3" small class="mr-50"/>
          <feather-icon v-else icon="UploadIcon" class="mr-50"/>
          Загрузить
        </b-button>
      </div>
    </b-modal>

    <!-- Source selection buttons -->
    <div class="d-flex justify-content-center mb-2">
      <b-button
          :variant="selectedSource === 'vk' ? 'primary' : 'outline-primary'"
          class="mr-1"
          @click="searchBySource('vk')"
      >
        ВКонтакте
      </b-button>
      <b-button
          :variant="selectedSource === 'youtube' ? 'danger' : 'outline-danger'"
          @click="searchBySource('youtube')"
      >
        YouTube
        <feather-icon
            v-if="trackInfo && trackInfo.has_yt_cache"
            v-b-tooltip.hover
            title="Результаты из кеша"
            icon="DatabaseIcon"
            size="14"
            class="ml-25"
        />
      </b-button>
      <b-button
          v-b-tooltip.hover
          title="Сбросить YouTube кеш"
          variant="outline-dark"
          class="ml-1"
          :disabled="!trackInfo || !trackInfo.has_yt_cache"
          @click="clearYoutubeCache"
      >
        <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />
        Сброс YT кеша
      </b-button>
    </div>

    <!-- Search results -->
    <b-row v-if="selectedSource">
      <b-col
          cols="12"
          md="12"
          class="d-flex align-items-center justify-content-center mb-2 mb-md-0"
      >
        <b-card
            title="Поиск песни"
            class="col-md-12 col-12"
        >
          <b-overlay
              :show="isLoadingTrack"
              rounded="sm"
          >
            <b-row>
              <b-col md="12">

                <div class="my-1 py-25">
                  <!-- Search error -->
                  <div v-if="searchError" class="text-center py-2">
                    <feather-icon icon="AlertTriangleIcon" size="48" class="text-danger mb-1"/>
                    <p class="text-danger font-weight-bold">{{ searchError }}</p>
                  </div>

                  <!-- VK results table -->
                  <b-table
                      v-else-if="selectedSource === 'vk'"
                      ref="refTracksListTable"
                      :items="searchResults"
                      responsive
                      :fields="vkTableColumns"
                      :tbody-tr-class="rowClass"
                      primary-key="id"
                      show-empty
                      empty-text="No tracks"
                      class="position-relative"
                  >

                    <!-- Column: Artist -->
                    <template #cell(artist)="data">
                      <b-media vertical-align="center" v-if="data.item.main_artists">
                        <template #aside v-if="data.item.album && data.item.album.thumb">
                          <b-avatar
                              size="64"
                              :src="data.item.album.thumb.photo_270"
                              :text="avatarText(data.item.main_artists[0].name)"
                              variant="primary"
                          />
                        </template>
                        <span class="font-weight-bold d-block text text-success" v-for="(artist, index) in data.item.main_artists">
                          {{ artist.name }}
                          <b-button
                              v-if="trackInfo && artist.name != trackInfo.artist"
                              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                              variant="info"
                              class="btn-icon"
                              size="sm"
                              @click="addAlias(trackInfo.artist_id, artist.name)"
                          >
                            <feather-icon icon="UserPlusIcon"/>
                          </b-button><span v-if="index < data.item.main_artists.length - 1">,</span>
                        </span>
                      </b-media>
                      <span class="text" v-else>
                        {{ data.item.artist }}
                      </span>
                    </template>

                    <!-- Column: Duration -->
                    <template #cell(duration)="data">
                      <span class="text-nowrap">
                        {{ formatDuration(data.item.duration) }}
                      </span>
                    </template>

                    <!-- Column: Title -->
                    <template #cell(title)="data">
                      {{ data.item.title }}
                      <span class="small" v-if="data.item.subtitle">
                        {{ data.item.subtitle }}
                      </span>
                      <b-button
                          v-if="trackInfo && ((data.item.artist != trackInfo.artist) || (data.item.title != trackInfo.track))"
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="warning"
                          class="btn-icon ml-50"
                          size="sm"
                          @click="updateTrackTitle(trackInfo.id, `${data.item.artist} - ${data.item.title}`)"
                      >
                        <feather-icon icon="ArrowUpCircleIcon"/>
                      </b-button>
                    </template>

                    <!-- Column: Date -->
                    <template #cell(date)="data">
                      <span class="text-nowrap">
                        {{ fromTimestamp(data.item.date) }}
                      </span>
                    </template>

                    <!-- Column: Actions -->
                    <template #cell(actions)="data">
                      <span class="text-nowrap">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="success"
                            class="btn-icon"
                            @click="downloadTrack(`${data.item.owner_id}_${data.item.id}`)"
                        >
                          <feather-icon icon="DownloadIcon"/>
                        </b-button>
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="primary"
                            class="btn-icon"
                            target="_blank"
                            :href="`https://vk.com/audio${data.item.owner_id}_${data.item.id}`"
                        >
                          <feather-icon icon="SearchIcon"/>
                        </b-button>
                      </span>
                    </template>
                  </b-table>

                  <!-- YouTube results table -->
                  <b-table
                      v-else-if="selectedSource === 'youtube'"
                      :items="searchResults"
                      responsive
                      :fields="youtubeTableColumns"
                      primary-key="youtube_external_id"
                      show-empty
                      empty-text="No tracks"
                      class="position-relative"
                  >

                    <!-- Column: Preview -->
                    <template #cell(preview)="data">
                      <img
                          v-if="data.item.preview"
                          :src="data.item.preview"
                          class="yt-preview-thumb"
                          @click="openPreview(data.item.preview)"
                      />
                    </template>

                    <!-- Column: Artist -->
                    <template #cell(artist)="data">
                      <span class="font-weight-bold text-success">
                        {{ data.item.artist }}
                      </span>
                    </template>

                    <!-- Column: Title -->
                    <template #cell(title)="data">
                      {{ data.item.title }}
                      <span class="small text-muted d-block" v-if="data.item.track && data.item.track !== data.item.title">
                        {{ data.item.track }}
                      </span>
                    </template>

                    <!-- Column: Channel -->
                    <template #cell(channel)="data">
                      {{ data.item.channel }}
                    </template>

                    <!-- Column: Duration -->
                    <template #cell(duration)="data">
                      <span class="text-nowrap" v-if="data.item.duration">
                        {{ formatDuration(data.item.duration) }}
                      </span>
                    </template>

                    <!-- Column: Date -->
                    <template #cell(release_datetime)="data">
                      <span class="text-nowrap">
                        {{ data.item.release_datetime }}
                      </span>
                    </template>

                    <!-- Column: Actions -->
                    <template #cell(actions)="data">
                      <span class="text-nowrap">
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="success"
                            class="btn-icon"
                            @click="downloadYoutubeTrack(data.item.youtube_external_id)"
                        >
                          <feather-icon icon="DownloadIcon"/>
                        </b-button>
                        <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="danger"
                            class="btn-icon ml-50"
                            target="_blank"
                            rel="noopener noreferrer"
                            :href="data.item.url || `https://www.youtube.com/watch?v=${data.item.youtube_external_id}`"
                        >
                          <feather-icon icon="ExternalLinkIcon"/>
                        </b-button>
                      </span>
                    </template>
                  </b-table>
                </div>
              </b-col>
            </b-row>
          </b-overlay>
        </b-card>
      </b-col>
    </b-row>

    <!-- YouTube cookies modal -->
    <b-modal
        v-model="showCookiesModal"
        title="Требуется обновление YouTube cookies"
        centered
        no-close-on-backdrop
        hide-footer
    >
      <b-alert show variant="danger" class="mb-1">
        {{ ytdlpErrorMessage }}
      </b-alert>
      <p class="mb-50">
        Вставьте содержимое cookie-файла в формате Netscape
        (экспортируйте через расширение «Get cookies.txt LOCALLY»):
      </p>
      <b-form-textarea
          v-model="cookiesText"
          rows="8"
          placeholder="# Netscape HTTP Cookie File&#10;.youtube.com&#9;TRUE&#9;/&#9;TRUE&#9;0&#9;SID&#9;value..."
          :disabled="isSendingCookies"
      />
      <div v-if="cookiesError" class="mt-1">
        <b-alert show variant="danger" class="mb-0 p-75">
          {{ cookiesError }}
        </b-alert>
      </div>
      <div class="d-flex justify-content-end mt-2">
        <b-button
            variant="outline-secondary"
            class="mr-1"
            :disabled="isSendingCookies"
            @click="closeCookiesModal"
        >
          Отмена
        </b-button>
        <b-button
            variant="primary"
            :disabled="!cookiesText || isSendingCookies"
            @click="submitCookies"
        >
          <b-spinner v-if="isSendingCookies" small class="mr-50"/>
          Отправить и повторить поиск
        </b-button>
      </div>
    </b-modal>

    <!-- Preview modal -->
    <div
        v-if="previewUrl"
        class="yt-preview-overlay"
        @click="closePreview"
    >
      <img :src="previewUrl" class="yt-preview-full"/>
    </div>
  </div>
</template>

<script>
import {
  BFormInput,
  BMedia,
  BAvatar,
  BInputGroup,
  BInputGroupAppend,
  BCard,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BOverlay,
  BTable,
  BModal,
  BAlert,
  BFormTextarea,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import {avatarText, formatDuration, fromTimestamp} from '@core/utils/filter'
import router from "@/router"
import store from "@/store"
import {ref} from "@vue/composition-api"
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BFormInput,
    BMedia,
    BAvatar,
    BInputGroup,
    BInputGroupAppend,
    BCard,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BOverlay,
    BTable,
    BModal,
    BAlert,
    BFormTextarea,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  props: {
    trackInfo: {
      type: Object,
      default: null,
    },
  },
  methods: {
    normalizeMatch(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/[^\wа-яё]+/gi, ' ')
        .trim()
    },
    rowClass(item, type) {
      if (!item || type !== 'row' || !this.trackInfo) return undefined

      const title = this.normalizeMatch(this.trackInfo.track)
      const artist = this.normalizeMatch(this.trackInfo.artist)
      const titleMatches = Boolean(title) && title === this.normalizeMatch(item.title)
      const artists = item.main_artists || [{name: item.artist}]
      const artistMatches = artists.some(candidate => artist && artist === this.normalizeMatch(candidate.name))

      if (titleMatches && artistMatches) return 'table-success'
      if (titleMatches || artistMatches) return 'table-warning'
      return undefined
    },
  },
  setup(props, { emit }) {
    const toast = useToast()

    const searchResults = ref([])
    const selectedSource = ref(null)
    const isLoadingTrack = ref(false)
    const previewUrl = ref(null)
    const searchError = ref(null)
    const downloadUrl = ref('')
    const isDownloadingByUrl = ref(false)

    // YouTube cookies
    const showCookiesModal = ref(false)
    const cookiesText = ref('')
    const cookiesError = ref(null)
    const isSendingCookies = ref(false)
    const ytdlpErrorMessage = ref('')

    // Upload MP3
    const showUploadModal = ref(false)
    const uploadFile = ref(null)
    const isDragOver = ref(false)
    const isUploadingMp3 = ref(false)
    const uploadError = ref(null)

    const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50 MB

    const formatFileSize = (bytes) => {
      if (bytes < 1024) return `${bytes} Б`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`
      return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
    }

    const validateFile = (file) => {
      if (!file) return 'Файл не выбран'
      if (file.type !== 'audio/mpeg' && !file.name.toLowerCase().endsWith('.mp3')) {
        return 'Поддерживается только формат MP3'
      }
      if (file.size > MAX_FILE_SIZE) {
        return `Размер файла превышает 50 МБ (${formatFileSize(file.size)})`
      }
      return null
    }

    const setUploadFile = (file) => {
      uploadError.value = null
      const error = validateFile(file)
      if (error) {
        uploadError.value = error
        return
      }
      uploadFile.value = file
    }

    const onFileDrop = (event) => {
      isDragOver.value = false
      const file = event.dataTransfer.files[0]
      if (file) setUploadFile(file)
    }

    const onFileSelect = (event) => {
      const file = event.target.files[0]
      if (file) setUploadFile(file)
      event.target.value = ''
    }

    const closeUploadModal = () => {
      showUploadModal.value = false
      uploadFile.value = null
      uploadError.value = null
      isDragOver.value = false
    }

    const uploadMp3 = async () => {
      if (!uploadFile.value) return
      isUploadingMp3.value = true
      uploadError.value = null
      const id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/uploadTrackMp3', {id, file: uploadFile.value})
        toast({
          component: ToastificationContent,
          props: {
            title: 'MP3 файл успешно загружен',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        closeUploadModal()
        emit('track-updated')
      } catch (error) {
        const errors = error.response?.data?.errors
        const message = errors
          ? Object.values(errors).flat().join('. ')
          : (error.response?.data?.message || 'Ошибка загрузки файла')
        uploadError.value = message
      } finally {
        isUploadingMp3.value = false
      }
    }

    // VK Table Columns
    const vkTableColumns = [
      {key: 'artist', sortable: true},
      {key: 'title', sortable: true},
      {key: 'duration', sortable: true},
      {key: 'is_licensed', sortable: true},
      {key: 'date', sortable: true},
      {key: 'actions'},
    ]

    // YouTube Table Columns
    const youtubeTableColumns = [
      {key: 'preview', label: 'Превью', sortable: false},
      {key: 'artist', label: 'Артист', sortable: true},
      {key: 'title', label: 'Название', sortable: true},
      {key: 'channel', label: 'Канал', sortable: true},
      {key: 'duration', label: 'Длительность', sortable: true},
      {key: 'release_datetime', label: 'Дата', sortable: true},
      {key: 'actions', label: 'Действия'},
    ]

    const searchBySource = async (source) => {
      selectedSource.value = source
      searchError.value = null
      isLoadingTrack.value = true
      try {
        let id = router.currentRoute.params.id
        const res = await store.dispatch('artists/searchTrack', {id: id, queryParams: {source}})
        if (res.data.error_code === 'ytdlp_error') {
          ytdlpErrorMessage.value = res.data.error || 'Ошибка yt-dlp'
          showCookiesModal.value = true
          searchResults.value = []
          searchError.value = 'Требуется обновление YouTube cookies. Нажмите кнопку в модальном окне для обновления.'
          return
        }
        searchResults.value = res.data.results || []
      } catch (error) {
        const responseData = error.response?.data
        if (responseData?.error_code === 'ytdlp_error') {
          ytdlpErrorMessage.value = responseData.error || 'Ошибка yt-dlp'
          showCookiesModal.value = true
          searchResults.value = []
          searchError.value = 'Требуется обновление YouTube cookies0000. Нажмите кнопку в модальном окне для обновления.'
          return
        }
        const message = responseData?.message || error.message || 'Ошибка поиска'
        searchError.value = message
        searchResults.value = []
        toast({
          component: ToastificationContent,
          props: {
            title: message,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const downloadTrack = async (vkId) => {
      isLoadingTrack.value = true
      let id = router.currentRoute.params.id
      try {
        await store.dispatch('artists/downloadTrack', {id: id, queryParams: {source: 'vk', vkId: vkId}})
        emit('track-updated')
        if (selectedSource.value) {
          await searchBySource(selectedSource.value)
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const clearYoutubeCache = async () => {
      const id = router.currentRoute.params.id
      isLoadingTrack.value = true
      try {
        await store.dispatch('artists/clearYoutubeCache', {id})
        toast({
          component: ToastificationContent,
          props: {
            title: 'YouTube кеш сброшен',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        emit('track-updated')
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сброса YouTube кеша',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const downloadYoutubeTrack = async (youtubeId) => {
      isLoadingTrack.value = true
      let id = router.currentRoute.params.id
      try {
        const res = await store.dispatch('artists/downloadTrackYoutube', {id: id, queryParams: {source: 'youtube', youtubeId: youtubeId}})
        if (res.data.error_code === 'ytdlp_error') {
          ytdlpErrorMessage.value = res.data.error || 'Ошибка yt-dlp'
          showCookiesModal.value = true
          return
        }
        emit('track-updated')
        if (selectedSource.value) {
          await searchBySource(selectedSource.value)
        }
      } catch (error) {
        const responseData = error.response?.data
        if (responseData?.error_code === 'ytdlp_error') {
          ytdlpErrorMessage.value = responseData.error || 'Ошибка yt-dlp'
          showCookiesModal.value = true
          return
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки трека с YouTube',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const parseDownloadUrl = (url) => {
      // VK: https://vk.com/audio-2001_123456 or https://vk.com/audio123_456
      const vkMatch = url.match(/vk\.com\/audio(-?\d+_\d+)/)
      if (vkMatch) {
        return { source: 'vk', id: vkMatch[1] }
      }

      // YouTube: https://www.youtube.com/watch?v=ID or https://youtu.be/ID
      const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/)
      if (ytMatch) {
        return { source: 'youtube', id: ytMatch[1] }
      }

      return null
    }

    const downloadByUrl = async () => {
      const url = downloadUrl.value.trim()
      if (!url) return

      const parsed = parseDownloadUrl(url)
      if (!parsed) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Не удалось определить тип ссылки. Поддерживаются ссылки VK и YouTube.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      isDownloadingByUrl.value = true
      try {
        if (parsed.source === 'vk') {
          await downloadTrack(parsed.id)
        } else {
          await downloadYoutubeTrack(parsed.id)
        }
        if (showCookiesModal.value) return
        downloadUrl.value = ''
        toast({
          component: ToastificationContent,
          props: {
            title: 'Трек успешно загружен',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        // ошибки обрабатываются внутри downloadTrack/downloadYoutubeTrack
      } finally {
        isDownloadingByUrl.value = false
      }
    }

    const updateTrackTitle = async (id, title) => {
      isLoadingTrack.value = true
      try {
        await store.dispatch('artists/updateTrack', {id: id, queryParams: {title: title}})
        emit('track-updated')
        if (selectedSource.value) {
          await searchBySource(selectedSource.value)
        }
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error updating track title',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const addAlias = async (artistId, alias) => {
      isLoadingTrack.value = true
      try {
        await store.dispatch('artists/addAlias', {id: artistId, queryParams: {alias: alias}})
        toast({
          component: ToastificationContent,
          props: {
            title: 'Алиас добавлен',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления алиаса',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoadingTrack.value = false
      }
    }

    const openPreview = (url) => {
      previewUrl.value = url
    }

    const closePreview = () => {
      previewUrl.value = null
    }

    const closeCookiesModal = () => {
      showCookiesModal.value = false
      cookiesText.value = ''
      cookiesError.value = null
    }

    const submitCookies = async () => {
      if (!cookiesText.value) return
      isSendingCookies.value = true
      cookiesError.value = null
      try {
        await store.dispatch('artists/updateYoutubeCookies', {cookies: cookiesText.value})
        toast({
          component: ToastificationContent,
          props: {
            title: 'YouTube cookies обновлены',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        closeCookiesModal()
        await searchBySource('youtube')
      } catch (error) {
        const message = error.response?.data?.error || error.message || 'Ошибка обновления cookies'
        cookiesError.value = message
      } finally {
        isSendingCookies.value = false
      }
    }

    return {
      searchResults,
      selectedSource,
      isLoadingTrack,
      searchError,
      downloadUrl,
      isDownloadingByUrl,
      downloadByUrl,
      // Upload MP3
      showUploadModal,
      uploadFile,
      isDragOver,
      isUploadingMp3,
      uploadError,
      formatFileSize,
      onFileDrop,
      onFileSelect,
      closeUploadModal,
      uploadMp3,
      searchBySource,
      downloadTrack,
      // YouTube cookies
      showCookiesModal,
      cookiesText,
      cookiesError,
      isSendingCookies,
      ytdlpErrorMessage,
      closeCookiesModal,
      submitCookies,
      clearYoutubeCache,
      downloadYoutubeTrack,
      updateTrackTitle,
      addAlias,
      vkTableColumns,
      youtubeTableColumns,
      avatarText,
      formatDuration,
      fromTimestamp,
      previewUrl,
      openPreview,
      closePreview,
    }
  },
}
</script>

<style lang="scss">
.upload-drop-zone {
  border: 2px dashed #d8d6de;
  border-radius: 8px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #7367f0;
    background-color: rgba(115, 103, 240, 0.04);
  }

  &.drop-zone-active {
    border-color: #7367f0;
    background-color: rgba(115, 103, 240, 0.08);
  }

  &.drop-zone-has-file {
    border-color: #28c76f;
    border-style: solid;
    background-color: rgba(40, 199, 111, 0.04);
  }

  &.drop-zone-uploading {
    pointer-events: none;
    opacity: 0.7;
  }
}

.yt-preview-thumb {
  width: 160px;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
}

.yt-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: pointer;
}

.yt-preview-full {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}
</style>
